// Libraries
import React from 'react';

// Supermove
import {DropdownInput, Icon, IconSource, PhoneInput, Space, Styled} from '@supermove/components';
import {getQueryName, gql} from '@supermove/graphql';
import {
  UrlFiltersType,
  ModalType,
  useResponsive,
  ResponsiveType,
  useNavigationDOM,
  useDrawer,
  useEffect,
  useRef,
} from '@supermove/hooks';
import {ProjectModel, Project, Job} from '@supermove/models';
import {colors, fontWeight, Typography} from '@supermove/styles';
import {Datetime, openNewTab, Phone} from '@supermove/utils';

// App
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import EditableField, {EditableFieldProps} from '@shared/design/components/Field/EditableField';
import Text from '@shared/design/components/Text';
import useUpdateProjectValue from '@shared/modules/Billing/hooks/useUpdateProjectValue';
import ProjectStatusBadge from '@shared/modules/Project/components/ProjectStatusBadge';
import ProjectBlockKind from '@shared/modules/Project/enums/ProjectBlockKind';
import ProjectDescriptionForm from '@shared/modules/Project/forms/ProjectDescriptionForm';
import UpdateProjectSinglePropertyForm from '@shared/modules/Project/forms/UpdateProjectSinglePropertyForm';
import useUpdateProjectClientContactEmailMutation from '@shared/modules/Project/hooks/useUpdateProjectClientContactEmailMutation';
import useUpdateProjectClientContactPhoneNumberMutation from '@shared/modules/Project/hooks/useUpdateProjectClientContactPhoneNumberMutation';
import useUpdateProjectClientNameMutation from '@shared/modules/Project/hooks/useUpdateProjectClientNameMutation';
import useUpdateProjectDescriptionMutation from '@shared/modules/Project/hooks/useUpdateProjectDescriptionMutation';
import Line from 'components/Line';
import CommunicationInboxFooterSendEmail from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxFooterSendEmail';
import CommunicationInboxProjectConversationFooter from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxProjectConversationFooter';
import EditProjectLocationsDrawer from 'modules/Project/V2/Show/Blocks/components/EditProjectLocationsDrawer';
import ProjectHeaderActions from 'modules/Project/V2/Show/components/ProjectHeaderActions';
import ProjectTags from 'modules/Project/V2/Show/components/ProjectTags';
import ProjectType from 'modules/Project/V2/Show/components/ProjectType';
import {ProjectPageUrlFiltersType} from 'modules/Project/V2/types/ProjectPageUrlFilters';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const MobileSubheadingText = Styled.Text`
  ${Typography.Mobile.Subheading}
`;

const MobileBodyText = Styled.Text`
  ${Typography.Mobile.Body}
`;

const projectOrgAndTypeMarginBottom = 8;
const ProjectOrgText = Styled.Text`
  ${Typography.Mobile.Micro}
  margin-right: 24px;
  margin-bottom: ${projectOrgAndTypeMarginBottom}px;
`;

const IconContainer = Styled.View<{size: number}>`
  justify-content: center;
  align-items: center;
  width: ${({size}) => size}px;
  height: ${({size}) => size}px;
`;

const WidgetOverviewSection = Styled.View`
`;

const ProjectBadgeRightPlaceholder = Styled.View`
  flex: 1;
`;

const ProjectNameButton = Styled.ButtonV2`
`;

const ProjectName = Styled.Text`
  ${Typography.Responsive.Link}
  ${fontWeight(700)}
  font-size: 16px;
  line-height: 24px;
`;

const ProjectDetailButton = ({
  icon,
  text,
  onPress,
}: {
  icon: IconSource;
  text: string;
  onPress: () => void;
}) => {
  return (
    <TertiaryButton
      style={{
        flex: 1,
        justifyContent: 'flex-start',
        paddingVertical: 12,
      }}
      onPress={onPress}
    >
      <IconContainer size={16}>
        <Icon source={icon} size={16} color={colors.blue.interactive} />
      </IconContainer>
      <Space width={8} />
      <MobileBodyText>{text}</MobileBodyText>
    </TertiaryButton>
  );
};

const EditableFieldComponent = (props: EditableFieldProps & {responsive: ResponsiveType}) => {
  return (
    <EditableField
      {...props}
      leftIconProps={{
        color: colors.gray.secondary,
        size: props.responsive.desktop ? 12 : 14,
        style: {marginRight: 4},
        ...props?.leftIconProps,
      }}
      valueProps={{empty: 'None', ...props.valueProps}}
      isHorizontal
    />
  );
};

const ClientNameField = ({
  project,
  refetch,
  responsive,
}: {
  project: ProjectModel;
  refetch: () => void;
  responsive: ResponsiveType;
}) => {
  const {form, handleSubmit} = useUpdateProjectClientNameMutation({
    updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
      projectId: project.id,
      property: 'name',
      stringValue: project.client.primaryContact?.fullName,
    }),
    onSuccess: refetch,
    onError: (errors) => console.log({errors}),
  });
  return (
    <EditableFieldComponent
      leftIcon={Icon.User}
      leftIconTooltip={'Customer name'}
      valueProps={{value: project.client.primaryContact?.fullName}}
      inputProps={{
        ...form,
        name: 'updateProjectSinglePropertyForm.stringValue',
        input: {placeholder: 'Enter customer name'},
      }}
      handleSubmit={handleSubmit}
      responseToasts={{displayName: 'Customer name'}}
      responsive={responsive}
    />
  );
};

const ClientEmailField = ({
  project,
  refetch,
  responsive,
}: {
  project: ProjectModel;
  refetch: () => void;
  responsive: ResponsiveType;
}) => {
  const {form, handleSubmit} = useUpdateProjectClientContactEmailMutation({
    updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
      projectId: project.id,
      property: 'email',
      stringValue: project.client.primaryContact.email,
    }),
    onSuccess: refetch,
    onError: ({errors}) => console.log(errors),
    refetchQueries: [
      getQueryName(CommunicationInboxProjectConversationFooter.query),
      getQueryName(CommunicationInboxFooterSendEmail.query),
    ],
  });
  return (
    <EditableFieldComponent
      leftIcon={Icon.Envelope}
      leftIconTooltip={'Email'}
      valueProps={{value: project.client.primaryContact.email}}
      inputProps={{
        ...form,
        name: 'updateProjectSinglePropertyForm.stringValue',
        input: {placeholder: 'Enter email'},
      }}
      handleSubmit={handleSubmit}
      responseToasts={{displayName: 'Email'}}
      responsive={responsive}
    />
  );
};

const ClientPhoneNumberField = ({
  project,
  refetch,
  responsive,
}: {
  project: ProjectModel;
  refetch: () => void;
  responsive: ResponsiveType;
}) => {
  const {form, handleSubmit} = useUpdateProjectClientContactPhoneNumberMutation({
    updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
      projectId: project.id,
      property: 'phoneNumber',
      stringValue: Phone.strip(project.client.primaryContact.phoneNumber),
    }),
    onSuccess: refetch,
    onError: (errors) => console.log({errors}),
  });
  return (
    <EditableFieldComponent
      leftIcon={Icon.Phone}
      leftIconTooltip={'Phone number'}
      valueProps={{value: Phone.display(project.client.primaryContact.phoneNumber)}}
      inputProps={{
        ...form,
        name: 'updateProjectSinglePropertyForm.stringValue',
        component: PhoneInput,
        input: {
          placeholder: 'Enter phone number',
          setFieldValue: form.setFieldValue,
        },
      }}
      handleSubmit={handleSubmit}
      responseToasts={{displayName: 'Phone number'}}
      responsive={responsive}
    />
  );
};

const CompanyField = ({
  project,
  responsive,
}: {
  project: ProjectModel;
  responsive: ResponsiveType;
}) => {
  return (
    <EditableFieldComponent
      leftIcon={Icon.Building}
      leftIconTooltip={'Company'}
      valueProps={{value: project.organization.name}}
      isDisabled
      responsive={responsive}
    />
  );
};

const ProjectTypeField = ({project}: {project: ProjectModel}) => {
  return (
    <Row style={{marginLeft: 4}}>
      <ProjectType.Dot color={project.projectType.color} tooltip={'Project type'} />
      <Space width={12} />
      <Text.Body>{project.projectType.name}</Text.Body>
    </Row>
  );
};

const ProjectOverviewWidgetSection = ({
  project,
  refetch,
  isSuperUser,
  responsive,
}: {
  project: ProjectModel;
  refetch: () => void;
  isSuperUser: boolean;
  responsive: ResponsiveType;
}) => {
  const handleProjectPress = () => {
    openNewTab(`${project.isStorage ? '/storage' : ''}/projects/${project.uuid}`);
  };

  return (
    <WidgetOverviewSection>
      <Row style={{flex: 1, justifyContent: 'space-between'}}>
        <ProjectNameButton onPress={handleProjectPress}>
          <ProjectName responsive={responsive}>{Project.getName(project)}</ProjectName>
        </ProjectNameButton>
        {!project.isStorage ? (
          <ProjectHeaderActions
            project={project}
            refetch={refetch}
            isSuperUser={isSuperUser}
            isWidget
          />
        ) : null}
      </Row>
      <Space height={16} />
      <Row>
        <ProjectStatusBadge project={project} style={{paddingVertical: 0, height: 24}} isEditable />
        <ProjectBadgeRightPlaceholder />
      </Row>
      <Space height={16} />
      <ClientNameField project={project} refetch={refetch} responsive={responsive} />
      <Space height={8} />
      <ClientEmailField project={project} refetch={refetch} responsive={responsive} />
      <Space height={8} />
      <ClientPhoneNumberField project={project} refetch={refetch} responsive={responsive} />
      <Space height={8} />
      <CompanyField project={project} responsive={responsive} />
      <Space height={8} />
      <ProjectTypeField project={project} />
    </WidgetOverviewSection>
  );
};

const Description = ({project, onSuccess}: {project: ProjectModel; onSuccess: () => void}) => {
  const {form, handleSubmit} = useUpdateProjectDescriptionMutation({
    projectDescriptionForm: ProjectDescriptionForm.toEdit({
      projectId: project.id,
      description: project.description,
    }),
    onSuccess,
    onError: (errors) => console.log({errors}),
  });

  return (
    <EditableField
      labelProps={{children: 'Description'}}
      valueProps={{value: project.description, empty: 'None'}}
      inputProps={{
        ...form,
        name: 'projectDescriptionForm.description',
        input: {multiline: true, style: {minHeight: 136, paddingTop: 8}},
      }}
      handleSubmit={handleSubmit}
      containerStyle={{marginVertical: 8}}
      responseToasts={{displayName: 'Description'}}
    />
  );
};

const ProjectSizeField = ({
  project,
  refetch,
  responsive,
}: {
  project: ProjectModel;
  refetch: () => void;
  responsive: ResponsiveType;
}) => {
  const originalSize = useRef(project.size);
  const {form, handleSubmitPress, refreshForm} = useUpdateProjectValue({project, refetch});

  // Project size can be updated from other parts of the page, so reset the form if it changes
  useEffect(() => {
    if (project.size !== originalSize.current) {
      originalSize.current = project.size;
      refreshForm();
    }
  }, [project.size, originalSize]);

  if (project.isStorage) {
    return null;
  }

  return (
    <EditableFieldComponent
      leftIcon={Icon.HouseBuilding}
      leftIconTooltip={'Project size'}
      valueProps={{value: project.size}}
      inputProps={{
        ...form,
        name: 'displayValuesForm.valueForms.0.stringValue',
        component: DropdownInput,
        input: {
          placeholder: 'Enter project size',
          options: project.valueByIdentifier.variable.variableOptions.map(({label, value}) => ({
            label,
            value,
          })),
          setFieldValue: form.setFieldValue,
          isPortaled: true,
          style: {width: '100%'},
        },
      }}
      responseToasts={{displayName: 'Project size'}}
      handleSubmit={handleSubmitPress}
      responsive={responsive}
    />
  );
};

const ProjectOverviewContentMobile = ({
  project,
  urlFilters,
  refetch,
  refetchAndReset,
  setShouldCloseOverviewSheetOnClickOutside,
  handleClose,
  editBillingValuesModal,
  isWidget,
  isSuperUser,
}: {
  project: ProjectModel;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  refetch: () => void;
  refetchAndReset: () => void;
  setShouldCloseOverviewSheetOnClickOutside?: (value: boolean) => void;
  handleClose?: () => void;
  editBillingValuesModal: ModalType;
  isWidget?: boolean;
  isSuperUser: boolean;
}) => {
  const responsive = useResponsive();
  const openEditBillingValuesModal = () => {
    editBillingValuesModal.handleOpen();
    handleClose?.();
  };
  const editProjectLocationsDrawer = useDrawer({
    name: 'Edit Project Locations Drawer Project Overview',
    enableTracking: true,
  });
  const {navigator} = useNavigationDOM();

  return (
    <React.Fragment>
      {isWidget ? (
        <ProjectOverviewWidgetSection
          project={project}
          refetch={refetch}
          isSuperUser={isSuperUser}
          responsive={responsive}
        />
      ) : (
        <MobileSubheadingText>{project.name || project.client.name}</MobileSubheadingText>
      )}
      <Space height={8} />
      {isWidget ? null : (
        <Row>
          <ProjectOrgText>{project.organization.name}</ProjectOrgText>
          <ProjectType
            projectType={project.projectType}
            style={{marginBottom: projectOrgAndTypeMarginBottom}}
          />
        </Row>
      )}
      {project.isCancelled && (
        <React.Fragment>
          <Space height={8} />
          <ProjectOrgText>{`Cancelled at: ${Datetime.convertToDisplayDatetime(project.cancelledAt)}`}</ProjectOrgText>
        </React.Fragment>
      )}
      {isWidget ? (
        <Description project={project} onSuccess={refetchAndReset} />
      ) : project.description ? (
        <React.Fragment>
          <Space height={8} />
          <MobileBodyText>{project.description}</MobileBodyText>
        </React.Fragment>
      ) : null}
      <Space height={16} />
      <ProjectTags
        project={project}
        refetchAndReset={refetchAndReset}
        setShouldCloseOverviewSheetOnClickOutside={setShouldCloseOverviewSheetOnClickOutside}
      />
      <Space height={8} />
      {isWidget ? (
        <React.Fragment>
          <EditableFieldComponent
            leftIcon={Icon.Calendar}
            isDisabled={project.isStorage}
            leftIconTooltip={'Start & End Date'}
            valueProps={{value: Project.getDate(project)}}
            handleEditPress={() => {
              const job = project.allJobsExcludingChildJobs.find((job) => !job.isCancelled);
              if (!job) {
                return;
              }
              Job.goToEditProjectJobs(job, navigator, {
                projectUuid: project.uuid,
                field: 'startDate',
              });
            }}
            responsive={responsive}
          />
          <Space height={8} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <ProjectDetailButton
            icon={Icon.Calendar}
            text={Project.getDate(project)}
            onPress={() => {
              urlFilters.handleReset({block: ProjectBlockKind.JOBS});
              handleClose && handleClose();
            }}
          />
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        <React.Fragment>
          <ProjectSizeField project={project} refetch={refetch} responsive={responsive} />
          <Space height={8} />
        </React.Fragment>
      ) : project.size ? (
        <React.Fragment>
          <ProjectDetailButton
            icon={Icon.HouseBuilding}
            text={project.size}
            onPress={() => {
              editBillingValuesModal.handleOpen();
              handleClose && handleClose();
            }}
          />
          <Line />
        </React.Fragment>
      ) : null}
      {isWidget ? (
        <React.Fragment>
          <EditableFieldComponent
            leftIcon={Icon.WeightHanging}
            leftIconTooltip={'Weight'}
            valueProps={{value: `${project.weight} lbs`}}
            handleEditPress={openEditBillingValuesModal}
            responsive={responsive}
          />
          <Space height={8} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <ProjectDetailButton
            icon={Icon.WeightHanging}
            text={`${project.weight} lbs`}
            onPress={openEditBillingValuesModal}
          />
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        project.isStorage ? null : (
          <React.Fragment>
            <EditableFieldComponent
              leftIcon={Icon.RulerTriangle}
              leftIconTooltip={'Volume'}
              valueProps={{value: `${project.volume} ft³`}}
              handleEditPress={openEditBillingValuesModal}
              responsive={responsive}
            />
            <Space height={8} />
          </React.Fragment>
        )
      ) : (
        <React.Fragment>
          <ProjectDetailButton
            icon={Icon.RulerTriangle}
            text={`${project.volume} ft³`}
            onPress={openEditBillingValuesModal}
          />
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        project.isStorage ? null : (
          <React.Fragment>
            <EditableFieldComponent
              leftIcon={Icon.MapPin}
              leftIconTooltip={'Origin & Destination'}
              valueProps={{value: Project.getLocation(project)}}
              handleEditPress={editProjectLocationsDrawer.handleOpen}
              responsive={responsive}
            />
            <Space height={8} />
            <EditProjectLocationsDrawer
              key={editProjectLocationsDrawer.key}
              isOpen={editProjectLocationsDrawer.isOpen}
              handleClose={editProjectLocationsDrawer.handleClose}
              project={project}
              refetch={refetch}
            />
          </React.Fragment>
        )
      ) : (
        <ProjectDetailButton
          icon={Icon.MapPin}
          text={Project.getLocation(project)}
          onPress={() => {
            urlFilters.handleReset({block: ProjectBlockKind.LOCATIONS});
            handleClose && handleClose();
          }}
        />
      )}
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------

ProjectOverviewContentMobile.fragment = gql`
  ${ProjectType.fragment}
  ${ProjectTags.fragment}
  ${Project.getDate.fragment}
  ${Project.getLocation.fragment}
  ${Project.getName.fragment}
  ${ProjectStatusBadge.fragment}
  ${ProjectHeaderActions.fragment}
  ${Job.goToEditProjectJobs.fragment}
  ${EditProjectLocationsDrawer.fragment}
  ${useUpdateProjectValue.fragment}

  fragment ProjectOverviewContentMobile on Project {
    id
    uuid
    name
    description
    weight
    volume
    size
    isCancelled
    cancelledAt
    isStorage
    organization {
      id
      name
    }
    client {
      id
      name
      primaryContact {
        id
        fullName
        email
        phoneNumber
      }
    }
    projectType {
      id
      color
      name
    }
    valueByIdentifier(identifier: "PROJECT_SIZE") {
      id
      variable {
        id
        variableOptions {
          label
          value
        }
      }
      ...useUpdateProjectValue_Value
    }
    allJobsExcludingChildJobs {
      id
      isCancelled
      ...Job_goToEditProjectJobs
    }
    ...ProjectType
    ...ProjectTags
    ...Project_getDate
    ...Project_getLocation
    ...Project_getName
    ...ProjectStatusBadge
    ...ProjectHeaderActions
    ...EditProjectLocationsDrawer
    ...useUpdateProjectValue_Project
  }
`;

export default ProjectOverviewContentMobile;
